import { isFulfilled } from "@reduxjs/toolkit";
import React, { JSX, useState } from "react";
import { useLocation } from "react-router";
import { toast } from "react-toastify";
import { NOTIFICATION_TIME_OUT } from "../../../constants/app";
import {
  formatAllowance,
  VACANCY_CANDIDATE_VISIBILITY,
  VACANCY_SELECTION_METHOD,
} from "../../../models/vacancy";
import { AUTH_ROUTE } from "../../../pages/App/routes";
import { applyForAJob } from "../../../services/candidateJobsService";
import { useAppDispatch, useAppSelector } from "../../../store";
import {
  hideTransparentPreloaderAction,
  showNotificationAction,
  showTransparentPreloaderAction,
} from "../../../store/application/slice";
import { TJobData } from "../../../store/candidate/types";
import { getCentreDetailsThunk } from "../../../store/superadmin/thunks";
import { convertTo12Hour } from "../../../utils/dataParser";
import CustomButton from "../../UI/Buttons/CustomButton";
import CircleCancel from "../../UI/Icons/CircleCancel";
import Location from "../../UI/Icons/Location";
import RoundedTick from "../../UI/Icons/RoundedTick";
import { formatTimeframes } from "../../Vacancy/timeframeApiHelper";
import DeclineApplyJobModal from "./AcceptedListing/DeclineApplyJobModal";
import { getCandidateLabel } from "./CandidateVacancyCard";
import VacancyDetails from "./Elements/VacancyDetails";
import AcceptJobModal from "./JobListings/ApplyJobModal";
import ConflictConfirmApply from "./JobListings/ConflictConfirmApply";
import WithdrawApplyJobModal from "./PendingListing/WithdrawApplyJobModal";

interface CalendarVacancyCardProps {
  /**
   * Vacancy data object containing details about the job vacancy.
   */
  vacancy: TJobData;
  /**
   * Type of the vacancy, which can be either "job" or "project".
   */
  type: string;
  /**
   * Function to fetch the candidate job list after applying for a job.
   * @returns void
   */
  getCandidateJobList?: () => void;

  isCandidateFilledAllForms?: boolean;

  isMobile?: boolean;
  dayAvailability?: any;
  setShowAcceptedJobs?: (show: boolean) => void;
  setShowAcceptJobModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowConfirmApplyJobModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowWithdrawApplyJobModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowConflictConfirmModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowDeclineApplyJobModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowCentreViewModal: React.Dispatch<React.SetStateAction<boolean>>;
  showConflictConfirmModal: boolean;
  showAcceptJobModal: boolean;
  showConfirmApplyJobModal: boolean;
  showWithdrawApplyJobModal: boolean;
  showDeclineApplyJobModal: boolean;
  showCentreViewModal: boolean;
  onRefresh?: () => void;
  index: number;
  setSelectedVacancy?: React.Dispatch<React.SetStateAction<TJobData | null>>;
  selectedVacancy?: TJobData | null;
  setShowJobAcceptedConfirmation?: React.Dispatch<React.SetStateAction<boolean>>;
  showJobAcceptedConfirmation?: boolean;
}

/**
 * `CandidateVacancyCard` is a React functional component that displays detailed information
 * about a job vacancy. It includes vacancy details, formatted timeframes, posting date,
 * and action buttons for interacting with the vacancy.
 *
 * @component
 * @param {CandidateVacancyCardProps} props - The props for the component.
 * @param {Vacancy} props.vacancy - The vacancy object containing details about the job.
 * @param {string} props.type - The type of the vacancy (e.g., full-time, part-time).
 *
 * @returns {JSX.Element} A styled card component displaying vacancy details and actions.
 *
 * @remarks
 * - The component uses `VacancyDetails` to render detailed information about the vacancy.
 * - Timeframes are formatted using the `formatTimeframes` function and displayed with conditional styling.
 * - Includes two action buttons: "View Profile" and "Accept".
 *
 * @example
 * ```tsx
 * <CalendarVacancyCardProps
 *   vacancy={{
 *     title: "Software Engineer",
 *     timeframes: [{ label: "2 days left", alert: true }],
 *     posted: "3 days ago",
 *   }}
 *   type="full-time"
 * />
 * ```
 */
const CalendarVacancyCard: React.FC<CalendarVacancyCardProps> = ({
  vacancy,
  type,
  getCandidateJobList,
  isCandidateFilledAllForms,
  isMobile = false,
  dayAvailability,
  setShowAcceptedJobs,
  setShowAcceptJobModal,
  setShowConfirmApplyJobModal,
  setShowWithdrawApplyJobModal,
  setShowConflictConfirmModal,
  setShowDeclineApplyJobModal,
  setShowCentreViewModal,
  showConflictConfirmModal,
  showAcceptJobModal,
  showConfirmApplyJobModal,
  showWithdrawApplyJobModal,
  showDeclineApplyJobModal,
  showCentreViewModal,
  onRefresh,
  index,
  setSelectedVacancy,
  selectedVacancy,
  setShowJobAcceptedConfirmation,
  showJobAcceptedConfirmation
}) => {
  const dispatch = useAppDispatch();
  const location = useLocation();

  const [isJobApplied, setIsJobApplied] = useState(false);
  const profileData = useAppSelector(
    (state) => state.onboarding.userpersonaldetails
  );
  const documentsExpired = useAppSelector(
    (state) => state.onboarding.userpersonaldetails?.is_documents_expired
  );

  const [isAddressCopied, setIsAddressCopied] = useState(false);

  /**
   * Handles the application process for a job vacancy.
   * @param vacancy - The vacancy object containing necessary details.
   * @returns void
   */
  const handleApplyForJob = async (vacancy: TJobData) => {
    dispatch(showTransparentPreloaderAction());
    try {
      const candidateUuid = profileData?.uuid;
      setSelectedVacancy && setSelectedVacancy(vacancy);

      if (!candidateUuid) {
        toast.error("Please complete your profile before applying for a job.");
        dispatch(hideTransparentPreloaderAction());
        return;
      }

      const actionType =
        getCandidateLabel({
          selectionMethod: vacancy.selection_method as VACANCY_SELECTION_METHOD,
          visibilityType:
            vacancy.visibility_type as VACANCY_CANDIDATE_VISIBILITY,
          visibilityUpgraded: vacancy.visibility_upgraded,
        }) === "Accept"
          ? "accepted"
          : "applied";

      const requestData = {
        action: actionType,
        uuid: vacancy?.uuid,
        candidate_uuid: candidateUuid ?? "",
        selection_method: vacancy?.selection_method,
        isResponseTimeGotOver: Boolean(vacancy?.visibility_upgraded),
      };

      const applyJobResponse = await applyForAJob(dispatch, requestData);

      const payload = (applyJobResponse?.payload ?? {}) as any;
      const restrictionReached = payload?.data?.apply_restricted === true;
      const timeframeConflict = payload?.data?.is_timeframe_conflict === true;

      if (isFulfilled(applyJobResponse)) {
        // toast.success("You have successfully applied for the job.");
        setShowAcceptJobModal(false);
        // setShowAcceptedJobs?.(false); // For calendar module apply job
        if (restrictionReached) {
          dispatch(
            showNotificationAction(
              "You can't apply more than five jobs in a day."
            )
          );
          return;
        } else if (actionType === "applied") {
          setShowConfirmApplyJobModal(true);
          // setShowAcceptedJobs?.(false); // For calendar module apply job
          // return;
        } else {
          // actionType === "accepted" && setShowAcceptedJobs?.(false);
          // dispatch(showNotificationAction("Job Accepted"));
          actionType === "accepted" && setShowJobAcceptedConfirmation && setShowJobAcceptedConfirmation(true);
          // setTimeout(() => {
          //   getCandidateJobList && getCandidateJobList();            
          //   onRefresh && onRefresh();
          // }, NOTIFICATION_TIME_OUT);
        }
        setIsJobApplied(true);
      } else {
        // setShowAcceptedJobs?.(false); // For calendar module apply job
        if (restrictionReached) {
          handleApplyRestriction();
        } else if (timeframeConflict) {
          handleTimeframeConflict(actionType);
        }
      }
    } catch (error) {
      console.error("Failed to apply for the job:", error);
    } finally {
      dispatch(hideTransparentPreloaderAction());
    }
  };

  const handleApplyRestriction = () => {
    setShowConflictConfirmModal(false);
    setShowAcceptJobModal(false);
    setShowAcceptedJobs?.(false);

    dispatch(
      showNotificationAction("You can't apply more than five jobs in a day.")
    );

    setTimeout(() => {
      // isMobile && setShowAcceptedJobs?.(true);
      setShowAcceptJobModal(false); // Close the modal after showing the notification
      onRefresh && onRefresh();
    }, NOTIFICATION_TIME_OUT);
    return;
  };

  const handleTimeframeConflict = (actionType: string) => {
    if (actionType === "accepted") {
      setShowAcceptJobModal(false);
      setShowAcceptedJobs?.(false);
      dispatch(
        showNotificationAction(
          "You cannot accept this job due to a conflicting timeframe."
        )
      );
      setTimeout(() => {
        onRefresh && onRefresh();
      }, NOTIFICATION_TIME_OUT);
    } else {
      setShowConflictConfirmModal(true);
    }
    return;
  };

  const handleConfirmJobApply = (vacancy: TJobData) => {
    // if(isMobile && setShowAcceptedJobs) { // For calendar module apply job
    //   setShowAcceptedJobs(false);
    // }
    setSelectedVacancy && setSelectedVacancy(vacancy);
    getCandidateLabel({
      selectionMethod: vacancy.selection_method as VACANCY_SELECTION_METHOD,
      visibilityType: vacancy.visibility_type as VACANCY_CANDIDATE_VISIBILITY,
      visibilityUpgraded: vacancy.visibility_upgraded,
    }) === "Accept" || (vacancy?.candidate_past_jobs_count === 0 && vacancy?.is_elv_centre)
      ? setShowAcceptJobModal(true)
      : handleApplyForJob(vacancy);
  };

  const handleWithdrawJobApplication = (vacancy: TJobData) => {
    setShowWithdrawApplyJobModal(true);
  };

  const handleDeclineJobApplication = (vacancy: TJobData) => {
    setShowDeclineApplyJobModal(true);
  };

  const handleView = async (id: number) => {
    setShowCentreViewModal(true);
    dispatch(showTransparentPreloaderAction());
    await dispatch(getCentreDetailsThunk({ centre_id: id }));
    dispatch(hideTransparentPreloaderAction());
  };

  const getActionButtons = (
    pathname: string,
    vacancy: TJobData,
    handleApplyForJob: (vacancy: TJobData) => void
  ) => {
    const buttonConfigs: Record<
      string,
      {
        label: string;
        className: string;
        icon: JSX.Element;
        onClick: () => void;
      }
    > = {
      [AUTH_ROUTE.JOBS]: {
        label: getCandidateLabel({
          selectionMethod: vacancy.selection_method as VACANCY_SELECTION_METHOD,
          visibilityType:
            vacancy.visibility_type as VACANCY_CANDIDATE_VISIBILITY,
          visibilityUpgraded: vacancy.visibility_upgraded,
        }),
        className: "btn-theme bg-teal text-black",
        icon: (
          <RoundedTick
            color={(isCandidateFilledAllForms && !documentsExpired && !vacancy.is_documents_expired) ? "black" : "#aaaaaa"}
          />
        ),
        onClick: () => handleConfirmJobApply(vacancy),
      },
      [AUTH_ROUTE.PENDING_JOBS]: {
        label: "Withdraw",
        className: "btn-theme bg-black text-white",
        icon: <CircleCancel fill="white" />,
        onClick: () => handleWithdrawJobApplication(vacancy),
      },
      [AUTH_ROUTE.ACCEPTED_JOBS]: {
        label: "Decline",
        className: "btn-theme bg-black text-white",
        icon: <CircleCancel fill="white" />,
        onClick: () => handleDeclineJobApplication(vacancy),
      },
    };

    return buttonConfigs[pathname] || null;
  };

  const definePathName = () => {
    if (type === "accepted") {
      return AUTH_ROUTE.ACCEPTED_JOBS;
    } else if (type === "jobs") {
      return AUTH_ROUTE.JOBS;
    } else {
      return location.pathname;
    }
  };

  const actionButton = getActionButtons(
    definePathName(),
    vacancy,
    handleApplyForJob
  );

  const handleCopyAddress = (address: string) => {
    if (address) {
      navigator.clipboard
        .writeText(address)
        .then(() => {
          setIsAddressCopied(true);
        })
        .catch((error) => {
          console.error("Failed to copy address:", error);
          dispatch(showNotificationAction("Failed to copy address."));
        });
    } else {
      dispatch(showNotificationAction("No address available to copy."));
    }
  };

  return (
    <>
      {index >= 1 && <div className="bg-off-white min-h-1px"></div>}
      <div className="d-flex flex-column gap-5">
        {isMobile &&
          type === "accepted" &&
          vacancy.timeframes &&
          vacancy.timeframes.length > 0 && (
            <>
              <div className="fs-16 fs-mobile-22 fw-700 text-black">
                {vacancy.timeframes
                  .filter(
                    (tf) => tf.start_time !== null && tf.end_time !== null
                  )
                  .map(
                    (tf) =>
                      `${convertTo12Hour(tf.start_time)} to ${convertTo12Hour(
                        tf.end_time
                      )}`
                  )
                  .join(", ")}
              </div>
              <div className="h-3px"></div>
            </>
          )}
        <div className="">
          {/**
           * Position name that match with candidate qualification
           */}

          <VacancyDetails
            vacancy={vacancy}
            isCandidateFilledAllForms={isCandidateFilledAllForms}
            documentsExpired={documentsExpired}
            isDocumentsExpired={vacancy.is_documents_expired}
            isMobile={isMobile}
            type={type}
          />

          {isMobile && type === "jobs" && (
            <div className="d-flex flex-column gap-10 ps-70px">
              <div className="fs-13 fw-400 text-black">
                <span className="fw-700">Requested Timeframe</span> <br />
                {vacancy?.timeframes &&
                  formatTimeframes(vacancy?.timeframes)?.map(
                    (formatedTimeData, index) => (
                      <div
                        key={index}
                        className={`fs-13 ${
                          formatedTimeData.alert
                            ? "text-alert fw-700"
                            : "fw-400"
                        }`}
                      >
                        {formatedTimeData.label}
                      </div>
                    )
                  )}
              </div>
              <div className="fs-13 fw-400 text-black">
                <span className="fw-700">Posted</span>
                <br />
                {vacancy?.posted ? vacancy?.posted : ""}
              </div>
              <div className="fs-13 fw-400 text-black">
                <span className="fw-700">Daily Allowance</span>
                <br />
                {formatAllowance(Number(vacancy?.allowance) ?? undefined, true)}
              </div> 
            </div>
          )}

          {/**
           * * Timeframes of the vacancy, formatted using the `formatTimeframes` function.
           */}
          {!isMobile && (
            <div className="ps-mobile-70px">
              <div className="fs-13 fw-700 d-lg-none">Requested Timeframe</div>
              <div className="min-w-210px w-210px mobile-w-100 text-break fs-13">
                {vacancy?.timeframes &&
                  formatTimeframes(vacancy?.timeframes)?.map(
                    (formatedTimeData, index) => (
                      <div
                        key={index}
                        className={`fs-13 ${
                          formatedTimeData.alert
                            ? "text-alert fw-700"
                            : "fw-400"
                        }`}
                      >
                        {formatedTimeData.label}
                      </div>
                    )
                  )}
              </div>
            </div>
          )}
          {/**
           * * Posted date of the vacancy.
           */}
          {!isMobile && (
            <div className="ps-mobile-70px">
              <div className="fs-13 fw-700 d-lg-none">Posted</div>
              <div className="min-w-100px fs-13">
                {vacancy?.posted ? vacancy?.posted : ""}
              </div>
            </div>
          )}

          {!isMobile && (
            <div className="ps-mobile-70px">
              <div className="fs-13 fw-700 d-lg-none">
                Daily Allowance
              </div>
              <div className="min-w-140px fs-13">{formatAllowance(Number(vacancy?.allowance) ?? undefined)}</div>
            </div>
          )}

          {type === "pending" && !isMobile && (
            <div className="ps-mobile-70px">
              <div className="fs-13 fw-700 d-lg-none">Applied</div>
              <div className="min-w-100px fs-13">
                {vacancy?.pivot?.applied_at_human
                  ? vacancy?.pivot?.applied_at_human
                  : ""}
              </div>
            </div>
          )}

          {type === "accepted" && !isMobile && (
            <div className="ps-mobile-70px">
              <div className="fs-13 fw-700 d-lg-none">Accepted</div>
              <div className="min-w-100px fs-13">
                {vacancy?.pivot?.accepted_at_human
                  ? vacancy?.pivot?.accepted_at_human
                  : ""}
              </div>
            </div>
          )}
          {/**
           * Action buttons for viewing the profile and accepting the vacancy.
           */}
        </div>
        {type !== "view_centre" && (
          <>
            <div className="h-10px"></div>
            <div className="d-flex flex-column flex-lg-row gap-20">
              {isMobile && type === "accepted" && (
                <>
                  <CustomButton
                    variant={"primary"}
                    type="button"
                    className="btn-theme bg-off-white text-black w-100"
                    onClick={() => {
                      handleCopyAddress(vacancy?.centre?.address);
                      setTimeout(() => setIsAddressCopied(false), 2000);
                    }}
                  >
                    <Location />
                    Copy Address
                  </CustomButton>
                </>
              )}
              {isAddressCopied && (
                <div className="text-teal-dark">Address copied</div>
              )}

              <CustomButton
                variant={"primary"}
                type="button"
                className={`btn-theme  text-black min-w-126px mobile-w-100 w-100 ${
                  isMobile && type === "jobs" ? "bg-teal" : "bg-off-white"
                }`}
                onClick={() => handleView(vacancy?.centre?.id)}
                disabled={(isCandidateFilledAllForms && !documentsExpired && !vacancy.is_documents_expired) ? false : true}
              >
                View Profile
              </CustomButton>

              {actionButton && (
                <CustomButton
                  variant={"primary"}
                  type="button"
                  className={` w-100 ${actionButton.className}`}
                  onClick={actionButton.onClick}
                  disabled={(isCandidateFilledAllForms && !documentsExpired && !vacancy.is_documents_expired) ? false : true}
                >
                  {actionButton.icon}
                  {actionButton.label}
                </CustomButton>
              )}
            </div>
          </>
        )}
      </div>
      {/* { showApplyJobModal && ( */}
      {selectedVacancy?.uuid === vacancy.uuid && (
        <AcceptJobModal
          showApplyJobModal={!!showAcceptJobModal}
          setShowApplyJobModal={setShowAcceptJobModal}
          vacancyData={vacancy}
          handleApplyForJob={handleApplyForJob}
        />
      )}
      {/* )} */}

      <WithdrawApplyJobModal
        getCandidateJobList={getCandidateJobList}
        showApplyJobModal={showWithdrawApplyJobModal}
        setShowApplyJobModal={setShowWithdrawApplyJobModal}
        vacancyData={vacancy}
        handleApplyForJob={handleApplyForJob}
      />

      <DeclineApplyJobModal
        getCandidateJobList={getCandidateJobList}
        showApplyJobModal={showDeclineApplyJobModal}
        setShowApplyJobModal={setShowDeclineApplyJobModal}
        vacancyData={vacancy}
        handleApplyForJob={handleApplyForJob}
        setShowAcceptedJobs={setShowAcceptedJobs}
        onRefresh={onRefresh}
      />

      {selectedVacancy?.uuid === vacancy.uuid && (
      <ConflictConfirmApply
        getCandidateJobList={getCandidateJobList}
        showApplyJobModal={showConflictConfirmModal}
        setShowApplyJobModal={setShowConflictConfirmModal}
        vacancyData={vacancy}
        handleApplyForJob={handleApplyForJob}
        showConfirmApplyJobModal={showConfirmApplyJobModal}
        setShowConfirmApplyJobModal={setShowConfirmApplyJobModal}
        showConflictConfirmModal={showConflictConfirmModal}
        setShowConflictConfirmModal={setShowConflictConfirmModal}
        isMobile={true}
        setShowAcceptedJobs={setShowAcceptedJobs}
        onRefresh={onRefresh}
      />
      )}

      

      
    </>
  );
};

export default CalendarVacancyCard;
